import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { VersionProvider } from "@/lib/VersionContext";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import DashboardPage from "@/pages/DashboardPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import ClientsPage from "@/pages/ClientsPage";
import TeamPage from "@/pages/TeamPage";
import ProfilePage from "@/pages/ProfilePage";
import InternalGuidePage from "@/pages/InternalGuidePage";
import DbConnectionsPage from "@/pages/admin/DbConnectionsPage";
import MigrationsPage from "@/pages/admin/MigrationsPage";
import EmailTemplatesPage from "@/pages/admin/EmailTemplatesPage";
import EnrolLeadsPage from "@/pages/admin/EnrolLeadsPage";
import DataChatPage from "@/pages/admin/DataChatPage";
import LifecycleAuditPage from "@/pages/admin/LifecycleAuditPage";
import DataStorageMapPage from "@/pages/admin/DataStorageMapPage";
import ClientGuidePage from "@/pages/ClientGuidePage";
import TestingPlanPage from "@/pages/TestingPlanPage";
import ClientOverviewPage from "@/pages/ClientOverviewPage";
import SupportPage from "@/pages/SupportPage";
import LeadsList from "@/pages/crm/LeadsList";
import LeadDetail from "@/pages/crm/LeadDetail";
import PendingLeadsPage from "@/pages/crm/PendingLeadsPage";
import QuarantineLeadsPage from "@/pages/crm/QuarantineLeadsPage";
import RepliesInboxPage from "@/pages/crm/RepliesInboxPage";
import BookingCheckInsPage from "@/pages/crm/BookingCheckInsPage";
import DripHealthPage from "@/pages/crm/DripHealthPage";
import ProductsList from "@/pages/mgmt/ProductsList";
import ProductForm from "@/pages/mgmt/ProductForm";
import QuotesList from "@/pages/quotes/QuotesList";
import QuoteBuilder from "@/pages/quotes/QuoteBuilder";
import QuoteDetail from "@/pages/quotes/QuoteDetail";
import RevisionHistoryPage from "@/pages/RevisionHistoryPage";
import InsightsPage from "@/pages/InsightsPage";
import SystemAdminPage from "@/pages/SystemAdminPage";
import SourceArchivePage from "@/pages/admin/SourceArchivePage";
import DbHygieneCleanupPage from "@/pages/admin/DbHygieneCleanupPage";
import MarketingOverview from "@/pages/marketing/MarketingOverview";
import CampaignsListPage from "@/pages/marketing/CampaignsListPage";
import CampaignEditorPage from "@/pages/marketing/CampaignEditorPage";
import CampaignReportPage from "@/pages/marketing/CampaignReportPage";
import ContactsPage from "@/pages/marketing/ContactsPage";
import ListsPage from "@/pages/marketing/ListsPage";
import SegmentsPage from "@/pages/marketing/SegmentsPage";
import ImportsListPage from "@/pages/marketing/ImportsListPage";
import CSVImportWizardPage from "@/pages/marketing/CSVImportWizardPage";
import SuppressionPage from "@/pages/marketing/SuppressionPage";
import MarketingSettingsPage from "@/pages/marketing/MarketingSettingsPage";
import { WorkflowsListPage, WorkflowEditorPage } from "@/pages/marketing/WorkflowsPage";
import SendForecastPage from "@/pages/marketing/SendForecastPage";
import BackupsPage from "@/pages/admin/BackupsPage";
import CompareCampaignsPage from "@/pages/marketing/CompareCampaignsPage";
import ApprovalsQueuePage from "@/pages/marketing/ApprovalsQueuePage";
import ContentBlocksPage from "@/pages/marketing/ContentBlocksPage";
import BrandAssetsPage from "@/pages/marketing/BrandAssetsPage";
import AnalyticsPage from "@/pages/marketing/AnalyticsPage";
import MarketingQueuePage from "@/pages/marketing/MarketingQueuePage";
import MarketingActivityPage from "@/pages/marketing/ActivityPage";
import ImportDetailPage from "@/pages/marketing/ImportDetailPage";
import PublicSpecPage from "@/pages/specs/PublicSpecPage";
import Eba2ControlPlanePage from "@/pages/eba2/Eba2ControlPlanePage";
import AppLayout from "@/components/layout/AppLayout";
import IdleTimer from "@/components/IdleTimer";
import "@/App.css";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-white" data-testid="loading-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent animate-spin" />
          <span className="text-sm text-[#525252] font-medium tracking-wide uppercase">Loading</span>
        </div>
      </div>
    );
  }
  if (!user) {
    // Session 69 — preserve the original deep-link target through the
    // login flow so emails like "Open Ticket SUP-0044" land the user
    // back on the ticket after they sign in, instead of dumping them
    // on a generic dashboard. LoginPage reads `?next=` and honours it
    // (same-origin only, blocks open-redirect injection).
    const target = location.pathname + (location.search || "");
    const isAlreadyRoot = location.pathname === "/" || location.pathname === "";
    const next = isAlreadyRoot ? "" : `?next=${encodeURIComponent(target)}`;
    return <Navigate to={`/${next}`} replace />;
  }
  return children;
}

// Owner-only gate. The frontend allowlist is just a UX hint — the backend
// is the actual security boundary (see backend/routes/downloads.py
// _require_owner). If we ever miss a user here, the API still returns
// 403 and the page shows a "Not authorized" placeholder.
const OWNER_EMAILS = (process.env.REACT_APP_OWNER_EMAILS || "mike@eyvo.com")
  .split(",").map(e => e.trim().toLowerCase()).filter(Boolean);

function OwnerOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-white" data-testid="loading-screen">
        <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  const email = (user.email || "").toLowerCase();
  if (!OWNER_EMAILS.includes(email)) {
    // Same UX as 404 — we don't want non-owners to even see the route exists.
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/signup" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      {/* Public spec review — accessed via encrypted share_token emailed to client. No auth. */}
      <Route path="/public/spec/:token" element={<PublicSpecPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><AppLayout><DashboardPage /></AppLayout></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><AppLayout><ProjectsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/projects/:projectId" element={<ProtectedRoute><AppLayout><ProjectDetailPage /></AppLayout></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><AppLayout><ClientsPage /></AppLayout></ProtectedRoute>} />
      {/* Retired global EBA 2.0 Tenants page (2026-08-18) — kept as a redirect notice only,
          for an existing bookmark. EBA provisioning now lives at Clients → [Client] → EBA Provisioning. */}
      <Route path="/eba2" element={<ProtectedRoute><AppLayout><Eba2ControlPlanePage /></AppLayout></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><AppLayout><TeamPage /></AppLayout></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><AppLayout><ProfilePage /></AppLayout></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><AppLayout><SupportPage /></AppLayout></ProtectedRoute>} />
      <Route path="/crm/leads" element={<ProtectedRoute><AppLayout><LeadsList /></AppLayout></ProtectedRoute>} />
      <Route path="/crm/replies" element={<ProtectedRoute><AppLayout><RepliesInboxPage /></AppLayout></ProtectedRoute>} />
      <Route path="/crm/booking-checks" element={<ProtectedRoute><AppLayout><BookingCheckInsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/crm/drip-health" element={<ProtectedRoute><AppLayout><DripHealthPage /></AppLayout></ProtectedRoute>} />
      <Route path="/crm/pending-leads" element={<ProtectedRoute><AppLayout><PendingLeadsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/crm/leads/quarantine" element={<ProtectedRoute><AppLayout><QuarantineLeadsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/crm/leads/:leadId" element={<ProtectedRoute><AppLayout><LeadDetail /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing" element={<ProtectedRoute><AppLayout><MarketingOverview /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/campaigns" element={<ProtectedRoute><AppLayout><CampaignsListPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/campaigns/new" element={<ProtectedRoute><AppLayout><CampaignEditorPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/campaigns/:campaignId" element={<ProtectedRoute><AppLayout><CampaignReportPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/campaigns/:campaignId/edit" element={<ProtectedRoute><AppLayout><CampaignEditorPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/analytics" element={<ProtectedRoute><AppLayout><AnalyticsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/queue" element={<ProtectedRoute><AppLayout><MarketingQueuePage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/activity" element={<ProtectedRoute><AppLayout><MarketingActivityPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/contacts" element={<ProtectedRoute><AppLayout><ContactsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/lists" element={<ProtectedRoute><AppLayout><ListsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/segments" element={<ProtectedRoute><AppLayout><SegmentsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/imports" element={<ProtectedRoute><AppLayout><ImportsListPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/imports/new" element={<ProtectedRoute><AppLayout><CSVImportWizardPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/imports/:batchId" element={<ProtectedRoute><AppLayout><ImportDetailPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/suppression" element={<ProtectedRoute><AppLayout><SuppressionPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/settings" element={<ProtectedRoute><AppLayout><MarketingSettingsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/workflows" element={<ProtectedRoute><AppLayout><WorkflowsListPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/workflows/:workflowId" element={<ProtectedRoute><AppLayout><WorkflowEditorPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/forecast" element={<ProtectedRoute><AppLayout><SendForecastPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/backups" element={<ProtectedRoute><AppLayout><BackupsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/compare" element={<ProtectedRoute><AppLayout><CompareCampaignsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/approvals" element={<ProtectedRoute><AppLayout><ApprovalsQueuePage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/content-blocks" element={<ProtectedRoute><AppLayout><ContentBlocksPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing/brand-assets" element={<ProtectedRoute><AppLayout><BrandAssetsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/mgmt/products" element={<ProtectedRoute><AppLayout><ProductsList /></AppLayout></ProtectedRoute>} />
      <Route path="/mgmt/products/new" element={<ProtectedRoute><AppLayout><ProductForm /></AppLayout></ProtectedRoute>} />
      <Route path="/mgmt/products/:productId/edit" element={<ProtectedRoute><AppLayout><ProductForm /></AppLayout></ProtectedRoute>} />
      <Route path="/quotes" element={<ProtectedRoute><AppLayout><QuotesList /></AppLayout></ProtectedRoute>} />
      <Route path="/quotes/new" element={<ProtectedRoute><AppLayout><QuoteBuilder /></AppLayout></ProtectedRoute>} />
      <Route path="/quotes/:quoteId" element={<ProtectedRoute><AppLayout><QuoteDetail /></AppLayout></ProtectedRoute>} />
      <Route path="/quotes/:quoteId/edit" element={<ProtectedRoute><AppLayout><QuoteBuilder /></AppLayout></ProtectedRoute>} />
      <Route path="/guide/internal" element={<ProtectedRoute><InternalGuidePage /></ProtectedRoute>} />
      <Route path="/admin/db-connections" element={<ProtectedRoute><DbConnectionsPage /></ProtectedRoute>} />
      <Route path="/admin/migrations" element={<ProtectedRoute><MigrationsPage /></ProtectedRoute>} />
      <Route path="/admin/email-templates" element={<ProtectedRoute><EmailTemplatesPage /></ProtectedRoute>} />
      <Route path="/admin/marketing/enrol-leads" element={<ProtectedRoute><EnrolLeadsPage /></ProtectedRoute>} />
      <Route path="/admin/data-chat" element={<ProtectedRoute><DataChatPage /></ProtectedRoute>} />
      <Route path="/admin/marketing/lifecycle-audit" element={<ProtectedRoute><LifecycleAuditPage /></ProtectedRoute>} />
      <Route path="/docs/data-storage-map" element={<ProtectedRoute><DataStorageMapPage /></ProtectedRoute>} />
      {/* Session 75 — Client Guide is PUBLIC so brand-new clients can read the
          onboarding instructions BEFORE they have an account. Content is
          read-only marketing/help copy — no security risk. */}
      <Route path="/guide/client" element={<ClientGuidePage />} />
      <Route path="/guide/testing" element={<ProtectedRoute><TestingPlanPage /></ProtectedRoute>} />
      <Route path="/clients/:clientId" element={<ProtectedRoute><ClientOverviewPage /></ProtectedRoute>} />
      <Route path="/revision-history" element={<ProtectedRoute><AppLayout><RevisionHistoryPage /></AppLayout></ProtectedRoute>} />
      <Route path="/insights" element={<ProtectedRoute><AppLayout><InsightsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AppLayout><SystemAdminPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/source-archive" element={<OwnerOnlyRoute><AppLayout><SourceArchivePage /></AppLayout></OwnerOnlyRoute>} />
      <Route path="/admin/db-hygiene/cleanup" element={<ProtectedRoute><AppLayout><DbHygieneCleanupPage /></AppLayout></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <BrowserRouter>
        <AuthProvider>
          <VersionProvider>
            <AppRoutes />
            <IdleTimer />
            <Toaster position="top-right" />
          </VersionProvider>
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
